import { widgets, getDefaultOrder } from './widgets';

/**
 * Widget registry - imported from centralized widgets definition
 * See ./widgets/index.js to add new widgets
 */
export const WIDGET_REGISTRY = widgets;

/**
 * Default widget order - auto-generated from widget definitions
 */
export const DEFAULT_WIDGET_ORDER = getDefaultOrder();

/**
 * Get widgets for a specific section with user preferences applied
 * @param {string} section - 'top' or 'body'
 * @param {Object} userInfo - User information including permissions
 * @param {Array} widgetOrder - User's preferred widget order
 * @param {Object} widgetVisibility - User's widget visibility preferences
 * @param {boolean} includeHidden - Whether to include hidden widgets (for drag mode)
 * @returns {Array} Filtered and ordered widget configs
 */
export const getWidgetsForSection = (section, userInfo, widgetOrder, widgetVisibility, includeHidden = false) => {
  const sectionOrder = widgetOrder?.[section] || DEFAULT_WIDGET_ORDER[section];

  // Get all widgets for this section
  const sectionWidgets = WIDGET_REGISTRY.filter(w => w.section === section);

  // Sort by user's preferred order
  const orderedWidgets = sectionOrder
    .map(id => sectionWidgets.find(w => w.id === id))
    .filter(Boolean); // Remove any undefined entries

  // Add any new widgets that aren't in the saved order yet
  const newWidgets = sectionWidgets.filter(
    w => !sectionOrder.includes(w.id),
  );
  const allWidgets = [...orderedWidgets, ...newWidgets];

  // Filter by permissions and visibility
  return allWidgets.filter(widget => {
    // Check permission
    if (widget.permission && !widget.permission(userInfo)) {
      return false;
    }

    // If includeHidden is true (drag mode), show all widgets regardless of visibility
    if (includeHidden) {
      return true;
    }

    // Otherwise, check visibility (use default if not set)
    const isVisible = widgetVisibility?.[widget.id] ?? widget.defaultVisible;
    return isVisible;
  });
};

/**
 * Get all widgets that user has permission to see (for settings UI)
 * @param {Object} userInfo - User information including permissions
 * @returns {Array} Widgets user can access
 */
export const getAvailableWidgets = (userInfo) => {
  return WIDGET_REGISTRY.filter(widget => {
    if (widget.permission) {
      return widget.permission(userInfo);
    }
    return true;
  });
};
